import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useResetPasswordPage } from './useResetPasswordPage';
import { fadeInUp } from './resetPasswordConstants';
import LegalAgreementLine from '../../components/LegalAgreementLine';

const ResetPasswordPage: React.FC = () => {
  const { email, setEmail, message, error, loading, handleSubmit, clearMessage, navigate } = useResetPasswordPage();

  if (message) {
    return (
      <div className="min-h-screen bg-slate-950">
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white min-h-screen flex items-center"
        >
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
            />
            <motion.div
              className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
            />
          </div>
          <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div
              className="bg-slate-900/75 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl text-center"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <div className="mx-auto flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-green-500/20 mb-4 sm:mb-6">
                <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" aria-hidden="true" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Check your email</h2>
              <p className="text-sm sm:text-base text-slate-300 mb-6 sm:mb-8">
                If an account with <span className="font-medium text-white">{email}</span> exists,
                <br />
                we've sent a password reset link.
                <br />
                The link will expire in 1 hour.
              </p>
              <div className="mt-6 sm:mt-8">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center items-center py-2.5 sm:py-3 px-4 sm:px-6 border border-transparent rounded-lg shadow-sm text-sm sm:text-base font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                >
                  Back to Login
                </button>
              </div>
              <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-slate-400">
                Didn't receive the email?{' '}
                <button type="button" onClick={clearMessage} className="font-medium text-cyan-400 hover:text-cyan-300">
                  Resend
                </button>
              </p>
            </motion.div>
          </div>
        </motion.section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white min-h-screen flex items-center"
      >
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/5 backdrop-blur-sm border border-white/10 mb-6"
              >
                <span className="h-2 w-2 rounded-full bg-cyan-400 mr-2"></span>
                Reset Your Password
              </motion.div>
              <motion.h1
                className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <span className="block">Forgot Your</span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Password?</span>
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                No worries! Just enter your email and we'll send you a link to reset your password.
              </motion.p>
              <motion.div
                className="flex items-center justify-center lg:justify-start space-x-4 mt-8 lg:mt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link to="/login" className="inline-flex items-center text-sm text-slate-300 hover:text-white">
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Login
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="bg-slate-900/75 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Reset Password</h2>
              {error && (
                <div className="bg-red-500/20 border border-red-400 text-red-100 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}
              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-950/70 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full flex justify-center items-center py-2.5 sm:py-3 px-4 sm:px-6 border border-transparent rounded-lg shadow-sm text-sm sm:text-base font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
              <LegalAgreementLine className="mt-4 sm:mt-6 text-xs text-center text-slate-400" />
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default ResetPasswordPage;
